import { Injectable, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, Subject, Subscription } from 'rxjs';
import { delay, filter, finalize, map, tap } from 'rxjs/operators';

import {
  APP_VERSION,
  BDG_PREFERENCES,
  BDG_SETTINGS,
  CAMPAIGN,
  Company,
  COMPANY_DATA,
  COMPANY_ID_KEY,
  COMPANY_PRICES,
  CreateNewOrderArrayObject,
  CUSTOMER_DATA,
  CUSTOMER_ID_KEY,
  DATE_FORMAT,
  DATETIME_LOGOUT,
  FEATURE_FLAGS,
  FIRST_DAY_CALENDAR,
  IMPERSONATED,
  IStatus,
  ITokenPayload,
  IUser,
  NotificationGlobalEvent,
  REFRESH_TOKEN_KEY,
  SECTOR_MODULE,
  TOKEN_KEY,
  TypeRol,
  USER_DATA,
  USER_EXTRAS,
  USER_ID_KEY,
  USER_INFO,
  USER_KEY,
  USER_MODE_KEY,
  VENDOR_ID_KEY,
} from '@tacliatech/types';

import { LoginError, LoginRequest } from '@web-frontend/shared/interfaces/auth';
import { StorageService } from '../storage/storage.service';
import { FcmService, UserService } from '..';
import { Router } from '@angular/router';
import { ModalLogoutComponent } from '@web-frontend/shared/components/modal-logout';
import { BudgetService } from '../budgets/budgets.service';
import { SocketService } from '../socket';
import { MatDialog } from '@angular/material/dialog';
import { PermissionService } from '../permissions';
import { PaymentsService } from '../../../core/admin/payments/services/payments.service';
import { CompanyModuleService, CompanyService } from '../company';
import { AnalyticsService } from '../analytics/analytics.service';
import AmplitudeEvents from 'src/types/amplitude.enum';
import { authRoutes } from '../../../core/auth/auth-routing.module';
import { ModulesService } from '../modules/modules.service';
import { shutdown } from '@intercom/messenger-js-sdk';
import { ToastService } from '../toast/toast.service';
import { decodeToken } from 'src/types-legacy/lib/utils/jwt.utils';

const MONTH_TIMEOUT = 2628000000;

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private loginSuccess = new Subject();
  private loginError = new Subject<LoginError>();
  private loginLoading = new Subject<boolean>();

  private userCache = new BehaviorSubject<IUser>(null);

  get user(): IUser {
    return this.userCache.getValue();
  }

  loginSuccess$ = this.loginSuccess.asObservable();
  loginError$ = this.loginError.asObservable();
  loginLoading$ = this.loginLoading.asObservable();

  user$ = this.userCache.asObservable().pipe(
    filter((res) => Boolean(res)),
    map((user) => ({
      ...user,
      role:
        typeof user?.role === 'string'
          ? [user.role]
          : Array.isArray(user?.role)
          ? user.role
          : [],
    }))
  );
  user1$ = this.userCache.asObservable();

  isVendor$ = this.user$.pipe(map((res) => res?.mode === 'vendor'));
  isCustomer$ = this.user$.pipe(map((res) => res?.mode === 'customer'));

  isAdmin$ = this.user$.pipe(
    map((res) => res?.role?.includes(TypeRol.ADMIN_ROLE))
  );

  isUser$ = this.user$.pipe(
    map((res) => res?.role?.includes(TypeRol.USER_ROLE))
  );

  userId$ = this.user$.pipe(map((res) => res?._id));

  isAdminOrUserRole$ = this.user$.pipe(
    map(
      (res) =>
        res?.role?.includes(TypeRol.ADMIN_ROLE) ||
        res?.role?.includes(TypeRol.USER_ROLE)
    )
  );

  isNoLoginUser$: Observable<boolean> = this.user$.pipe(
    map((user) => this.isNoLoginEmail(user.email))
  );

  private sub$ = new Subscription();
  tokenSubscription = new Subscription();
  private flightUserRequestObs$!: Observable<IUser>;

  public registerLogin$ = new BehaviorSubject<LoginRequest>({
    username: '',
    password: '',
  });

  updateBannerDemo(value) {
    this.userCache.value.bannerDemo = value;
  }

  constructor(
    private http: HttpClient,
    private userService: UserService,
    private router: Router,
    private dialog: MatDialog,
    private budgetService: BudgetService,
    private socketService: SocketService,
    private permissionService: PermissionService,
    private paymentsService: PaymentsService,
    private companyService: CompanyService,
    private injector: Injector,
    private fcmService: FcmService,
    private analyticsService: AnalyticsService,
    private modulesService: ModulesService,
    private toastService: ToastService
  ) {}

  set updateUserCache(data: IUser) {
    this.userCache.next(data);
  }

  init() {
    this.handleUserRequest().subscribe(() => {
      this.watchSocketModulesChange();
    });
  }

  handleUserRequest(): Observable<IUser> {
    const isUserCached = Boolean(this.userCache.getValue());

    let obsFlight$: Observable<IUser> = isUserCached
      ? new Observable((obs) => {
          obs.next(this.userCache.getValue());
          obs.complete();
        })
      : this.requestUser();

    obsFlight$ = obsFlight$.pipe(
      finalize(() => {
        this.flightUserRequestObs$ = null;
      })
    );

    if (this.flightUserRequestObs$) {
      return this.flightUserRequestObs$;
    }

    this.flightUserRequestObs$ = obsFlight$;

    return obsFlight$;
  }

  refreshCacheUserRequest() {
    this.updateUserCache = null;
    return this.handleUserRequest();
  }

  private requestUser() {
    return this.http.get<IUser>(`:API_URL/users/${StorageService.UserId}`).pipe(
      tap((res) => {
        this.updateUserCache = res;
        const userData = {
          id: res.id,
          name: res.name,
          email: res.email,
          phone: res.phone,
          company: res.company._id,
          role: res.role?.[0],
          created_at: res.created_at,
          country: res.country,
        };
        localStorage.setItem('userData', JSON.stringify(userData));
      })
    );
  }

  private showModalLogout() {
    const modalRef = this.dialog.open(ModalLogoutComponent);
    (modalRef.componentInstance as ModalLogoutComponent).type = 'logout';
  }

  private waitingExpiration(timeout) {
    this.tokenSubscription.unsubscribe();
    this.tokenSubscription = of(null)
      .pipe(delay(timeout))
      .subscribe(() => {
        this.showModalLogout();
      });
  }

  async switchCompany(companyId: string, reloadWindow = false): Promise<void> {
    try {
      if (!companyId) throw new Error('Company ID is required');
      const token = StorageService.Token;

      const { accessToken } = await this.http
        .post<{ accessToken: string }>(`:API_BASE_URLuser/tokens`, {
          companyId,
          grantType: 'switch_company',
          adminToken: token,
        })
        .toPromise();

      await this.loginByToken(accessToken, !reloadWindow);
      if (reloadWindow) window.location.reload();
      return Promise.resolve();
    } catch (error) {
      this.toastService.show({
        text: 'header.menu.errorSwitchCompany',
        type: 'error',
      });
      return Promise.reject(error);
    }
  }

  goToSelectCompany(companies: string[]): void {
    if (companies.length > 1) this.router.navigate([authRoutes.selectCompany]);
  }

  async loginByToken(accessToken: string, postLogin = true): Promise<void> {
    try {
      this.logout();
      // eslint-disable-next-line no-empty
    } catch (e) {}
    StorageService.SetItem(TOKEN_KEY, accessToken);
    const decoded = decodeToken(accessToken);
    const user: IUser = await this.http
      .get<IUser>(`:API_URL/users/${decoded.sub}`)
      .toPromise();

    const companyId = decoded.currentCompanyId || user.company?._id;
    const storage = {
      id: user._id,
      username: user.name,
      customer_id: user._id,
      vendor_id: null,
      company_id: companyId,
      mode: user.mode || 'customer',
      ok: true,
      msj: null,
      access_token: accessToken,
      refresh_token: null,
      extras: user.extras,
    };
    this.prepareStorage(storage);
    if (postLogin) await this.loginSuccessfullyFlow();
  }

  public async impersonate(accessToken: string): Promise<void> {
    await this.loginByToken(accessToken);
    StorageService.SetItem(IMPERSONATED, true);
  }

  public async loginSuccessfullyFlow(): Promise<void> {
    this.init();

    await Promise.all([
      this.permissionService.init(),
      this.modulesService.init().toPromise(),
    ]);
    this.companyService.init();
    this.budgetService.init();
    this.companyService.getCompanyInfo().subscribe((res) => {
      const user: IUser = { ...this.user, company: res };
      this.updateUserCache = user;
    });
    this.loginSuccess.next();
  }

  logout() {
    this.analyticsService.trackEvent({
      sources: ['amplitude'],
      eventName: AmplitudeEvents.profile_endSession_end,
    });

    StorageService.Remove(TOKEN_KEY);
    StorageService.Remove(USER_ID_KEY);
    StorageService.Remove(USER_KEY);
    StorageService.Remove(REFRESH_TOKEN_KEY);
    StorageService.Remove(COMPANY_ID_KEY);
    StorageService.Remove(CUSTOMER_ID_KEY);
    StorageService.Remove(VENDOR_ID_KEY);
    StorageService.Remove(USER_MODE_KEY);
    StorageService.Remove(USER_EXTRAS);
    StorageService.Remove(APP_VERSION);
    StorageService.Remove(COMPANY_DATA);
    StorageService.Remove(CUSTOMER_DATA);
    StorageService.Remove(USER_DATA);
    StorageService.Remove(DATE_FORMAT);
    StorageService.Remove(BDG_PREFERENCES);
    StorageService.Remove(BDG_SETTINGS);
    StorageService.Remove(COMPANY_ID_KEY);
    StorageService.Remove(DATETIME_LOGOUT);
    StorageService.Remove(IMPERSONATED);
    StorageService.Remove(COMPANY_PRICES);
    StorageService.Remove(FIRST_DAY_CALENDAR);
    StorageService.Remove(SECTOR_MODULE);
    StorageService.Remove(USER_INFO);
    StorageService.Remove(CAMPAIGN);
    StorageService.Remove(FEATURE_FLAGS);

    this.injector.get<AnalyticsService>(AnalyticsService).logoutOrReset();
    this.resolveRoom(NotificationGlobalEvent.ClientLeave);
    this.companyService.clearCompanyCache();
    this.tokenSubscription.unsubscribe();
    this.socketService.off();
    this.fcmService.close();
    this.dialog.closeAll();
    this.userCache.next(null);
    this.userService.clearUserStorage();
    this.budgetService.clearSettingsCache();
    this.permissionService.clearPermissionStorage();
    this.injector
      .get<CompanyModuleService>(CompanyModuleService)
      .clearModuleStorage();
    shutdown();
  }

  checkUserMode(user: IUser) {
    if (StorageService.userMode && user.mode === null) {
      user.mode =
        StorageService.userMode === 'customer' ? 'customer' : 'vendor';
      // @ts-ignore
      this.updateMode(user._id, user.mode);
    }
  }

  getUser() {
    this.user$.subscribe((data) => {
      if (data) {
        this.checkUserMode(data);
      }
    });
  }

  updateMode(id: string, m: string) {
    // @ts-ignore
    this.sub$.add(this.userService.updateOne(id, { mode: m }).subscribe());
  }

  refresh() {
    return this.http
      .post<ITokenPayload>(`:API_URL/login/refresh`, {
        refreshToken: StorageService.GetItem(REFRESH_TOKEN_KEY),
      })
      .pipe(
        tap((res) => {
          this.prepareStorage(res);
        })
      );
  }
  getToken(data: any) {
    return this.http.post<any>(`:API_URL/auth/get-token`, data);
  }

  private prepareStorage(res: ITokenPayload) {
    this.waitingExpiration(MONTH_TIMEOUT);
    StorageService.SetItem(TOKEN_KEY, res.access_token);
    StorageService.SetItem(USER_ID_KEY, res.id);
    StorageService.SetItem(
      DATETIME_LOGOUT,
      res.id + '@' + new Date().valueOf()
    );
    StorageService.SetItem(USER_MODE_KEY, res.mode || 'customer');
    StorageService.SetItem(USER_KEY, res.username);
    StorageService.SetItem(REFRESH_TOKEN_KEY, res.refresh_token);
    StorageService.SetItem(COMPANY_ID_KEY, res.company_id);
    StorageService.SetItem(CUSTOMER_ID_KEY, res.customer_id);
    StorageService.SetItem(VENDOR_ID_KEY, res.vendor_id);
    StorageService.SetItem(USER_EXTRAS, JSON.stringify(res?.extras));
  }

  applyAutoLogout() {
    let dateTimeLocalStorage;
    let userStorageID;
    const auxValue = StorageService.GetItem(DATETIME_LOGOUT);
    if (auxValue) {
      const dateNow = new Date();
      userStorageID = +auxValue.split('@')[0];
      dateTimeLocalStorage = +auxValue.split('@')[1];

      const result = dateNow.valueOf() - dateTimeLocalStorage.valueOf();

      if (result >= MONTH_TIMEOUT) {
        this.showModalLogout();
      } else {
        return true;
      }
    } else {
      this.logout();
      this.router.navigate([authRoutes.login]);
    }

    return false;
  }

  private watchSocketModulesChange() {
    this.socketService.socketConnected$
      .pipe(filter((res) => Boolean(res)))
      .subscribe(() => {
        this.resolveRoom(NotificationGlobalEvent.ClientEnter);
        this.watchNotifications();
      });
  }

  resolveRoom(
    evt:
      | NotificationGlobalEvent.ClientEnter
      | NotificationGlobalEvent.ClientLeave
  ) {
    this.socketService.emit(evt, {
      room: StorageService.GetItem(COMPANY_ID_KEY),
    });
  }

  private async watchNotifications() {
    this.sub$.add(
      this.socketService
        .listen(NotificationGlobalEvent.ModulesChange)
        // @ts-ignore
        .subscribe((res: Company.Output) => {
          const user: IUser = { ...this.user, company: res };
          this.updateUserCache = user;
        })
    );
    // Listen for stripe status account
    this.sub$.add(
      this.socketService
        .listen(NotificationGlobalEvent.RefreshStripeStatusAccount)
        .subscribe(() => {
          this.paymentsService.init();
        })
    );
  }

  updateCompanyInfo(newStatusOrder: IStatus[]) {
    return this.companyService
      .findOneAndUpdate(StorageService.CompanyId, {
        orderStatus: CreateNewOrderArrayObject(newStatusOrder),
        customerId: StorageService.CustomerId,
      })
      .pipe(
        tap((res) => {
          const user: IUser = { ...this.user, company: res };
          this.updateUserCache = user;
        })
      );
  }

  isNoLoginEmail(email: string): boolean {
    const regex = /^no-login\+.*@taclia\.com$/;
    return regex.test(email);
  }
}
